import { Tooltip } from "react-tooltip";
import { useEffect, useState } from "react";
import { DataTable, DataTableStateEvent, SortOrder } from "primereact/datatable";
import { Column } from "primereact/column";
import { CheckCircleIcon, NoSymbolIcon } from "@heroicons/react/24/outline";
import SearchFilter from "../../../../components/input/SearchFilter";
import { Loading } from "../../../../components/Loading";
import SimplePagination from "../../../../components/SimplePagination";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import FormattedDateAndTime from "../../../../components/FormattedDateAndTime";
import Avatar from "../../../../components/Avatar";
import { DistrictAdminViewModel, useAdminListByDistrictQuery } from "../../districtsApiSlice";

interface DistrictAdminsProps {
	districtId: number;
}

export default function DistrictAdminsSection(props: DistrictAdminsProps) {
	const [page, setPage] = useState(0);
	const [sortField, setSortField] = useState("");
	const [sortOrder, setSortOrder] = useState<SortOrder>(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [isFiltered, setIsFiltered] = useState(false);

	const adminListResult = useAdminListByDistrictQuery(props.districtId);
	const data = adminListResult.data;

	// Filter admins locally based on search filter
	const filteredAdmins =
		data?.filter((admin) => {
			if (!searchFilter) return true;
			const searchLower = searchFilter.toLowerCase();
			return admin.formalFullName?.toLowerCase().includes(searchLower);
		}) ?? [];

	const sortedAdmins = [...filteredAdmins].sort((a, b) => {
		// User-selected sort: by chosen column
		if (!sortField || sortOrder === 0 || sortOrder == null) return 0;

		const aValue = a[sortField as keyof DistrictAdminViewModel];
		const bValue = b[sortField as keyof DistrictAdminViewModel];

		// Handle null/undefined values
		if (aValue == null && bValue == null) return 0;
		if (aValue == null) return sortOrder;
		if (bValue == null) return -sortOrder;

		// Compare values
		if (aValue < bValue) return -sortOrder;
		if (aValue > bValue) return sortOrder;
		return 0;
	});

	const handleSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 0);
		setPage(0);
	};

	useEffect(() => {
		setIsFiltered(!!searchFilter);
		setPage(0);
	}, [searchFilter]);

	const lastSignedInBodyTemplate = (row: DistrictAdminViewModel) => {
		return (
			<span>
				<FormattedDateAndTime date={row.lastLoginDate} />
			</span>
		);
	};

	const avatarBodyTemplate = (row: DistrictAdminViewModel) => {
		return (
			<span className="flex gap-4 items-center">
				<Avatar initials={row.initials} hasImage={row.hasImage} userId={row.id} />
				<span>{row.formalFullName}</span>
			</span>
		);
	};

	const activeBodyTemplate = (row: DistrictAdminViewModel) => {
		const tooltipContent = row.disabled ? "Disabled" : "Enabled";
		return (
			<span className="flex justify-center">
				{!row.disabled ? (
					<CheckCircleIcon
						className="size-6 text-green-500"
						data-tooltip-id={`enabled-${row.id}-tooltip`}
					/>
				) : (
					<NoSymbolIcon
						className="size-6 text-red-500"
						data-tooltip-id={`enabled-${row.id}-tooltip`}
					/>
				)}
				<Tooltip
					id={`enabled-${row.id}-tooltip`}
					place="top-start"
					content={tooltipContent}
				/>
			</span>
		);
	};

	// Pagination
	const pageSize = 10;
	const paginatedAdmins = sortedAdmins.slice(page * pageSize, (page + 1) * pageSize);
	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	return (
		<section>
			<hr className="my-6" />
			<h2>District Administrators</h2>
			<div className="flex justify-between items-end">
				<div className="flex gap-3 mt-4 items-end">
					<SearchFilter
						id="fullNameSearch"
						name="fullNameSearch"
						searchFilter={searchFilter}
						setSearchFilter={setSearchFilter}
						label="Search"
						placeHolder="Search by name..."
						className="w-80"
					/>
				</div>
			</div>
			<Loading loading={adminListResult.isLoading} />
			{data && (
				<DataTable
					value={paginatedAdmins}
					dataKey="id"
					emptyMessage="No district administrators found"
					stripedRows
					sortField={sortField}
					sortOrder={sortOrder}
					onSort={handleSort}
					sortIcon={CustomSortIcon}
					className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
					style={{ marginTop: "16px" }}>
					<Column
						header="Status"
						body={activeBodyTemplate}
						headerClassName="!w-12"
						className="!w-12 !text-center"
						sortable
						sortField="disabled"
					/>

					<Column
						header="Full Name"
						body={avatarBodyTemplate}
						field="formalFullName"
						sortable
						sortField="formalFullName"
					/>
					<Column header="User Name" field="userName" sortable sortField="userName" />
					<Column
						header="Phone Number"
						field="phoneNumber"
						sortable
						sortField="phoneNumber"
					/>
					<Column header="Email" field="email" sortable sortField="email" />
					<Column
						header="Last Login Date"
						body={lastSignedInBodyTemplate}
						field="lastLoginDate"
						sortable
						sortField="lastLoginDate"
					/>
				</DataTable>
			)}
			<SimplePagination
				itemCount={sortedAdmins.length}
				page={page}
				pageSize={pageSize}
				totalCount={data?.length}
				isFiltered={isFiltered}
				onChange={(page) => handlePageChange(page)}
				className={"border-none"}
			/>
		</section>
	);
}
